import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { FoldersDataService, Form, Field } from '../folders-data.service';
import { ApiService } from '../../../api.service';

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss']
})
export class DynamicFormComponent implements OnInit, OnDestroy {
  @Input() folderName: string = '';
  @Input() formRoute: string = '';

  form: FormGroup;
  formConfig: Form | undefined;
  loading: boolean = true;
  error: string = '';
  
  // Query and review functionality
  tabs = [
    { title: 'Query', type: 'query' },
    { title: 'Field History', type: 'fieldHistory' }
  ];
  
  fieldHistoryData: any[] = [];
  selectedTab = 0;
  isOptionsModalOpen = false;
  isModalOpen = false;
  showReviewModal = false;
  isReasonModalOpen = false;
  editReason = '';
  fieldLabel = '';
  
  queries: { threadId: string; text: string; isClosed: boolean; auditTrail: { user: string; role: string; timestamp: string; message: string }[] }[] = [
    { threadId: this.generateThreadId(), text: '', isClosed: false, auditTrail: [] }
  ];
  
  reviewOptions = {
    dataMonitor: false,
    monitor: false,
    pi: false
  };

  private subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private foldersDataService: FoldersDataService,
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit(): void {
    this.loadFormConfiguration();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private loadFormConfiguration(): void {
    if (!this.folderName || !this.formRoute) {
      this.error = 'Folder name and form route are required';
      this.loading = false;
      return;
    }

    this.subscription.add(
      this.foldersDataService.getFormByRoute(this.folderName, this.formRoute).subscribe({
        next: (formConfig) => {
          if (formConfig) {
            this.formConfig = formConfig;
            this.buildForm();
            this.loading = false;
          } else {
            this.error = 'Form configuration not found';
            this.loading = false;
          }
        },
        error: (err) => {
          this.error = 'Error loading form configuration';
          this.loading = false;
          console.error('Error loading form:', err);
        }
      })
    );
  }

  private buildForm(): void {
    if (!this.formConfig) return;

    const formControls: any = {};
    
    this.formConfig.fields.forEach(field => {
      const fieldName = this.getFieldName(field.title);
      
      switch (field.type) {
        case 'radio-button':
        case 'dropdown':
          formControls[fieldName] = [''];
          break;
        case 'checkbox-group':
          formControls[fieldName] = [[]];
          break;
        case 'number':
          formControls[fieldName] = [null];
          break;
        case 'compound':
          field.fields?.forEach(subField => {
            const subFieldName = this.getFieldName(subField.title);
            formControls[subFieldName] = [null];
          });
          break;
        default:
          formControls[fieldName] = [''];
      }

      // Add validators
      if (field.required) {
        formControls[fieldName] = [formControls[fieldName][0], Validators.required];
      }
    });

    this.form = this.fb.group(formControls);
  }

  private getFieldName(title: string): string {
    return title.replace(/\s+/g, '_').toLowerCase();
  }



  onSubmit(): void {
    if (this.form.valid) {
      console.log('Form submitted:', this.form.value);
      // Handle form submission
      this.submitFormData();
    } else {
      this.markFormGroupTouched();
    }
  }

  private submitFormData(): void {
    const formData = this.form.value;
    // Add your API call here
    console.log('Submitting form data:', formData);
  }

  private markFormGroupTouched(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
    });
  }

  // Query and review functionality
  openOptionsModal(event: Event, target: any): void {
    event.stopPropagation();
    this.fieldLabel = (target as HTMLElement).closest('.form-group')?.getAttribute('data-label') || '';
    this.isOptionsModalOpen = true;
  }

  openModal(): void {
    this.isModalOpen = true;
    this.isOptionsModalOpen = false;
  }

  closeModal(): void {
    this.isModalOpen = false;
  }

  openReviewModal(): void {
    this.showReviewModal = true;
    this.isOptionsModalOpen = false;
  }

  closeReviewModal(): void {
    this.showReviewModal = false;
  }

  editStudy(): void {
    this.isReasonModalOpen = true;
    this.isOptionsModalOpen = false;
  }

  cancelEditReason(): void {
    this.isReasonModalOpen = false;
    this.editReason = '';
  }

  confirmEditReason(): void {
    // Handle edit reason confirmation
    console.log('Edit reason:', this.editReason);
    this.isReasonModalOpen = false;
    this.editReason = '';
  }

  selectTab(index: number): void {
    this.selectedTab = index;
  }

  addQuery(index: number): void {
    this.queries.push({
      threadId: this.generateThreadId(),
      text: '',
      isClosed: false,
      auditTrail: []
    });
  }

  confirmSubmitQuery(index: number): void {
    // Handle query submission
    console.log('Submitting query:', this.queries[index]);
  }

  confirmCloseQuery(index: number): void {
    this.queries[index].isClosed = true;
  }

  onReviewCheckboxChange(): void {
    // Handle review checkbox changes
  }

  private generateThreadId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  // Helper methods for field rendering
  isFieldType(field: Field, type: string): boolean {
    return field.type === type;
  }

  getFieldValues(field: Field): any[] {
    return field.values || [];
  }

  getFieldUnit(field: Field): string {
    return field.unit || '';
  }

  getCompoundFields(field: Field): Field[] {
    return field.fields || [];
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/folders']);
  }
}
